import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { PROJECT_TEMPLATES, ProjectTemplate, getCategoryColor, getCategoryIcon } from '../data/projectTemplates';
import { Task, ProjectInfo } from '../types';

export interface Project {
  id: string;
  name: string;
  description: string;
  date: string;
  lastModified: string;
  matrixType?: 'RAPID' | 'RASCI'; // Optional for backward compatibility
}

interface ProjectData {
  tasks: Task[];
  projectInfo: ProjectInfo;
}

interface ProjectListProps {
  projects: Project[];
  onProjectsChange: (projects: Project[]) => void;
  onSelectProject: (projectId: string) => void;
  onCreateProjectWithData?: (project: Project, projectData: ProjectData) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({ 
  projects, 
  onProjectsChange, 
  onSelectProject,
  onCreateProjectWithData
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    date: new Date().toLocaleDateString(),
    matrixType: 'RAPID' as 'RAPID' | 'RASCI'
  });
  const [dialogTab, setDialogTab] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleCreateProject = () => {
    const project: Project = {
      id: `project-${Date.now()}`,
      name: newProject.name,
      description: newProject.description,
      date: newProject.date,
      lastModified: new Date().toLocaleDateString(),
      matrixType: newProject.matrixType
    };

    // Create initial project data with one default task
    const defaultTask: Task = {
      id: `task-${Date.now()}`,
      workArea: '',
      taskDescription: '',
      assignments: {}
    };

    const projectData: ProjectData = {
      tasks: [defaultTask],
      projectInfo: {
        date: newProject.date,
        projectSummary: `${newProject.name} - ${newProject.matrixType} Matrix`
      }
    };

    // Use the callback to create project with data
    if (onCreateProjectWithData) {
      onCreateProjectWithData(project, projectData);
    } else {
      onProjectsChange([...projects, project]);
    }

    setNewProject({ name: '', description: '', date: new Date().toLocaleDateString(), matrixType: 'RAPID' });
    setOpenDialog(false);
  };

  const handleCreateFromTemplate = () => {
    if (!selectedTemplate) return;
    
    const project: Project = {
      id: `project-${Date.now()}`,
      name: newProject.name || selectedTemplate.name,
      description: newProject.description || selectedTemplate.description,
      date: newProject.date,
      lastModified: new Date().toLocaleDateString(),
      matrixType: selectedTemplate.matrixType || newProject.matrixType
    };
    
    // Create project data with template tasks
    const projectData = {
      tasks: selectedTemplate.tasks.map((task, index) => ({
        ...task,
        id: `task-${Date.now()}-${index}`
      } as Task)),
      projectInfo: {
        ...selectedTemplate.projectInfo,
        date: newProject.date
      }
    };
    
    // Use the new callback if available, otherwise fallback to old method
    if (onCreateProjectWithData) {
      onCreateProjectWithData(project, projectData);
    } else {
      onProjectsChange([...projects, project]);
      // Store the template data in localStorage for the new project (fallback)
      const existingProjectData = JSON.parse(localStorage.getItem('rapid-project-data') || '{}');
      localStorage.setItem('rapid-project-data', JSON.stringify({
        ...existingProjectData,
        [project.id]: projectData
      }));
    }
    
    resetDialog();
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setNewProject({
      name: project.name,
      description: project.description,
      date: project.date,
      matrixType: project.matrixType || 'RAPID'
    });
    setOpenDialog(true);
  };

  const handleUpdateProject = () => {
    if (editingProject) {
      const updatedProject = {
        ...editingProject,
        name: newProject.name,
        description: newProject.description,
        date: newProject.date,
        lastModified: new Date().toLocaleDateString()
      };
      onProjectsChange(projects.map(p => p.id === editingProject.id ? updatedProject : p));
      setEditingProject(null);
      setNewProject({ name: '', description: '', date: new Date().toLocaleDateString(), matrixType: 'RAPID' });
      setOpenDialog(false);
    }
  };

  const handleDeleteProject = (projectId: string) => {
    onProjectsChange(projects.filter(p => p.id !== projectId));
  };

  const resetDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
    setNewProject({ name: '', description: '', date: new Date().toLocaleDateString(), matrixType: 'RAPID' });
    setDialogTab(0);
    setSelectedTemplate(null);
  };

  const handleOpenDialog = () => {
    resetDialog();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    resetDialog();
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          RAPID Decision Making Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{ backgroundColor: '#27ae60' }}
        >
          Create New Project
        </Button>
      </Box>

        {/* Search Bar */}
        {projects.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#666', mr: 1 }} />,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                )
              }}
              sx={{ minWidth: 300 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {filteredProjects.length} of {projects.length} projects
            </Typography>
          </Box>
        )}

        {projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No projects yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create your first project to get started with RAPID (Decision Making) or RASCI (Task Responsibility) matrices
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{ backgroundColor: '#27ae60' }}
            >
              Create Your First Project
            </Button>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 3 
          }}>
            {filteredProjects.map((project) => (
              <Card 
                key={project.id}
                elevation={3}
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      {project.name}
                    </Typography>
                    <Chip
                      label={project.matrixType || 'RAPID'}
                      size="small"
                      icon={project.matrixType === 'RASCI' ? <AssignmentIcon /> : <SpeedIcon />}
                      sx={{
                        backgroundColor: project.matrixType === 'RASCI' ? '#7b1fa2' : '#1976d2',
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {project.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {project.date}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Modified: {project.lastModified}
                    </Typography>
                  </Box>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditProject(project)}
                      sx={{ color: '#3498db' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteProject(project.id)}
                      sx={{ color: '#e74c3c' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<LaunchIcon />}
                    onClick={() => onSelectProject(project.id)}
                    sx={{ backgroundColor: '#2c3e50' }}
                  >
                    Open Project
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProject ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent>
          {!editingProject && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={dialogTab} onChange={(_, newValue) => setDialogTab(newValue)}>
                <Tab label="Blank Project" />
                <Tab label="From Template" />
              </Tabs>
            </Box>
          )}
          
          {dialogTab === 0 || editingProject ? (
            <Box sx={{ pt: 1 }}>
              {!editingProject && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Select Matrix Type
                  </Typography>
                  <ToggleButtonGroup
                    value={newProject.matrixType}
                    exclusive
                    onChange={(_, value) => value && setNewProject({ ...newProject, matrixType: value })}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="RAPID" sx={{ py: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <SpeedIcon sx={{ mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>RAPID</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          Decision Making - Who decides what
                        </Typography>
                      </Box>
                    </ToggleButton>
                    <ToggleButton value="RASCI" sx={{ py: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <AssignmentIcon sx={{ mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>RASCI</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          Task Responsibility - Who does what
                        </Typography>
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              <TextField
                fullWidth
                label="Project Name"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Project Description"
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Date"
                value={newProject.date}
                onChange={(e) => setNewProject({ ...newProject, date: e.target.value })}
              />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Select Matrix Type
                </Typography>
                <ToggleButtonGroup
                  value={newProject.matrixType}
                  exclusive
                  onChange={(_, value) => value && setNewProject({ ...newProject, matrixType: value })}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="RAPID" sx={{ py: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <SpeedIcon sx={{ mb: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>RAPID</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        Decision Making - Who decides what
                      </Typography>
                    </Box>
                  </ToggleButton>
                  <ToggleButton value="RASCI" sx={{ py: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <AssignmentIcon sx={{ mb: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>RASCI</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        Task Responsibility - Who does what
                      </Typography>
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Typography variant="h6" sx={{ mb: 1 }}>
                Choose a {newProject.matrixType} Template
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {PROJECT_TEMPLATES.filter(t => t.matrixType === newProject.matrixType).length} templates available for {newProject.matrixType} matrices
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
                {PROJECT_TEMPLATES.filter(t => t.matrixType === newProject.matrixType).map((template) => (
                  <Card
                    key={template.id}
                      variant={selectedTemplate?.id === template.id ? "outlined" : "elevation"}
                      elevation={selectedTemplate?.id === template.id ? 0 : 1}
                      sx={{
                        cursor: 'pointer',
                        border: selectedTemplate?.id === template.id ? 2 : 0,
                        borderColor: selectedTemplate?.id === template.id ? getCategoryColor(template.category) : 'transparent',
                        '&:hover': { elevation: 3 }
                      }}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontSize: '1.5rem', mr: 1 }}>
                            {getCategoryIcon(template.category)}
                          </Typography>
                          <Chip
                            label={template.category}
                            size="small"
                            sx={{ 
                              backgroundColor: getCategoryColor(template.category),
                              color: 'white',
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {template.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {template.tasks.length} pre-configured tasks
                        </Typography>
                      </CardContent>
                    </Card>
                ))}
              </Box>
              
              {selectedTemplate && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>Customize Your Project</Typography>
                  <TextField
                    fullWidth
                    label="Project Name"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder={selectedTemplate.name}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Project Description"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    placeholder={selectedTemplate.description}
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Date"
                    value={newProject.date}
                    onChange={(e) => setNewProject({ ...newProject, date: e.target.value })}
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={
              editingProject 
                ? handleUpdateProject 
                : dialogTab === 0 
                  ? handleCreateProject 
                  : handleCreateFromTemplate
            }
            variant="contained"
            disabled={
              editingProject 
                ? !newProject.name.trim()
                : dialogTab === 0 
                  ? !newProject.name.trim()
                  : !selectedTemplate
            }
          >
            {editingProject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectList; 